import { Box } from "@mui/material";
import React, { ReactNode } from "react";
import useTheme from "../../../../../common/hooks/useTheme";
import XIconSvg from "../../../../../common/svgs/XIconSvg";
import { Theme } from "../../../../../providers/ThemeProvider";
import RoundedIconContainer from "../../common/RoundedIconContainer";
import ScrollableWrapper from "../../common/ScrollableWrapper";
import SidePanel from "../../common/SidePanel";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
};

/** Narrower than the platform's 600px default. */
const PANEL_WIDTH = 450;

/**
 * The platform `SidePanel` with the chrome the plan drawers (nutrition and workout) share: a pinned
 * header (title + the platform's X close, as the prescription and biomarker
 * drawers use) and a scrolling body. 450px wide, 16px padding, dims the page.
 */
const PlanSidePanel = ({ open, onClose, title, children }: Props) => {
  const theme: Theme = useTheme();

  return (
    <SidePanel open={open} onClose={onClose} showBackdrop={true} width={PANEL_WIDTH}>
      <Box sx={styles.header(theme)}>
        <Box sx={styles.title(theme)}>{title}</Box>
        <RoundedIconContainer handleClick={onClose}>
          <XIconSvg />
        </RoundedIconContainer>
      </Box>
      <Box sx={styles.body}>
        <ScrollableWrapper customPadding="8px">
          <Box sx={styles.content}>
            {children}
            <Box sx={{ height: "60px" }} />
          </Box>
        </ScrollableWrapper>
      </Box>
    </SidePanel>
  );
};

export default PlanSidePanel;

const styles = {
  header: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    flexShrink: 0,
  }),
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "24px",
  }),
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
    padding: "0 0 0 16px",
  },
  content: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    paddingTop: "16px",
    // No padding of its own on the right: the ScrollableWrapper's 8px padding
    // plus its 8px reserved scrollbar gutter already make the 16px edge.
    paddingRight: 0,
  },
};
